/**
 * Pure assembler for the Post-Visit Summary (PVS) — PROD-1285.
 *
 * Faithful to Flutter `post_visit_summary_standalone`: the patient-facing recap
 * of the visit, assembled from the encounter note (GetAllEncounterDetails) —
 * chief complaint(s) + diagnoses + medications. Soft-deleted entries are dropped.
 */
import type { EncounterClinicalDetails } from '@/billing/services/use-clinical-note';

export interface PvsDiagnosis {
  label: string;
  primary: boolean;
}

export interface PostVisitSummary {
  chiefComplaints: string[];
  diagnoses: PvsDiagnosis[];
  medications: string[];
}

const live = <T extends { isDeleted?: boolean | null }>(x: T): boolean => x.isDeleted !== true;
const clean = (s: string | null | undefined): string => (s ?? '').trim();

export function toPostVisitSummary(
  details: EncounterClinicalDetails | null | undefined,
): PostVisitSummary {
  const chiefComplaints = (details?.chiefComplaints?.complaints ?? [])
    .map((c) => clean(c.coding?.display))
    .filter((s) => s.length > 0);

  const conditions = (details?.assessmentResponse ?? [])
    .flatMap((a) => a.assessmentPlanCondition ?? [])
    .filter(live);

  const diagnoses: PvsDiagnosis[] = conditions
    .map((c) => ({ label: clean(c.conditionCode?.display), primary: c.isPrimary === true }))
    .filter((d) => d.label.length > 0)
    // primary diagnoses first
    .sort((a, b) => Number(b.primary) - Number(a.primary));

  // Medications can attach to multiple diagnoses (one nested copy per Dx,
  // PROD-1428) → de-dupe by code, not by display. Code before identifier: the
  // server mints a distinct row id per nesting, so ids never match across
  // copies. Label prefers the display name but falls back to the code so a med
  // with a blank display is never silently dropped from the patient-facing summary.
  const medications: string[] = [];
  const seenMeds = new Set<string>();
  for (const m of conditions.flatMap((c) => c.childMedicalCode?.assessmentPlanMedication ?? []).filter(live)) {
    const label = clean(m.medicationCode?.display) || clean(m.medicationCode?.code);
    if (!label) continue; // nothing renderable
    const key = clean(m.medicationCode?.code) || clean(m.identifier) || label;
    if (seenMeds.has(key)) continue;
    seenMeds.add(key);
    medications.push(label);
  }

  return { chiefComplaints, diagnoses, medications };
}

/** True when the summary has nothing to show (drives the empty state). */
export function isPostVisitSummaryEmpty(s: PostVisitSummary): boolean {
  return s.chiefComplaints.length === 0 && s.diagnoses.length === 0 && s.medications.length === 0;
}
